import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useRouterState,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { ApiError } from "./api";
import { companyMeQueryOptions, identityQueryOptions } from "./companyContext";
import {
  discardPostLoginReturn,
  rememberPostLoginReturn,
} from "./postLoginReturn";

const AppShell = lazy(async () => ({
  default: (await import("./components/AppShell")).AppShell,
}));
const LoginPage = lazy(async () => ({
  default: (await import("./pages/AuthPages")).LoginPage,
}));
const AcceptInvitationPage = lazy(async () => ({
  default: (await import("./pages/AuthPages")).AcceptInvitationPage,
}));
const BoardActivationPage = lazy(async () => ({
  default: (await import("./pages/BoardActivationPage")).BoardActivationPage,
}));
const DashboardPage = lazy(async () => ({
  default: (await import("./pages/DashboardPage")).DashboardPage,
}));
const VoucherListPage = lazy(async () => ({
  default: (await import("./pages/VoucherListPage")).VoucherListPage,
}));
const VoucherEditorPage = lazy(async () => ({
  default: (await import("./pages/VoucherPages")).VoucherEditorPage,
}));
const InvoiceListPage = lazy(async () => ({
  default: (await import("./pages/InvoicePages")).InvoiceListPage,
}));
const InvoiceEditorPage = lazy(async () => ({
  default: (await import("./pages/InvoicePages")).InvoiceEditorPage,
}));
const DocumentUploadPage = lazy(async () => ({
  default: (await import("./pages/InvoicePages")).DocumentUploadPage,
}));
const UploadEntryPage = lazy(async () => ({
  default: (await import("./pages/QuickDocumentUploadPage")).UploadEntryPage,
}));
const QuickDocumentUploadPage = lazy(async () => ({
  default: (await import("./pages/QuickDocumentUploadPage")).QuickDocumentUploadPage,
}));
const FiscalYearsPage = lazy(async () => ({
  default: (await import("./pages/ReportFiscalPages")).FiscalYearsPage,
}));
const OpeningBalanceEditPage = lazy(async () => ({
  default: (await import("./pages/ReportFiscalPages")).OpeningBalanceEditPage,
}));
const ReportsPage = lazy(async () => ({
  default: (await import("./pages/ReportFiscalPages")).ReportsPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import("./pages/ReportFiscalPages")).SettingsPage,
}));
const UsersPage = lazy(async () => ({
  default: (await import("./pages/UsersPage")).UsersPage,
}));
const ChartOfAccountsPage = lazy(async () => ({
  default: (await import("./pages/ChartOfAccountsPage")).ChartOfAccountsPage,
}));
const TransactionAccountListPage = lazy(async () => ({
  default: (await import("./pages/TransactionAccountPages")).TransactionAccountListPage,
}));
const NewTransactionAccountPage = lazy(async () => ({
  default: (await import("./pages/TransactionAccountPages")).NewTransactionAccountPage,
}));
const TransactionAccountDetailPage = lazy(async () => ({
  default: (await import("./pages/TransactionAccountPages")).TransactionAccountDetailPage,
}));
const BoardPage = lazy(async () => ({
  default: (await import("./pages/BoardPage")).BoardPage,
}));
const OwnersPage = lazy(async () => ({
  default: (await import("./pages/AssociationRegisterPages")).OwnersPage,
}));
const OwnerDetailPage = lazy(async () => ({
  default: (await import("./pages/AssociationRegisterPages")).OwnerDetailPage,
}));
const PropertiesPage = lazy(async () => ({
  default: (await import("./pages/AssociationRegisterPages")).PropertiesPage,
}));
const PropertyDetailPage = lazy(async () => ({
  default: (await import("./pages/AssociationRegisterPages")).PropertyDetailPage,
}));
const RoadsPage = lazy(async () => ({ default: (await import("./pages/RoadPages")).RoadsPage }));
const FacilitiesPage = lazy(async () => ({
  default: (await import("./pages/AssociationFacilityPages")).FacilitiesPage,
}));
const FacilityDetailPage = lazy(async () => ({
  default: (await import("./pages/AssociationFacilityPages")).FacilityDetailPage,
}));
const TonKilometerListPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerListPage,
}));
const TonKilometerIndexPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerIndexPage,
}));
const NewTonKilometerIndexPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).NewTonKilometerIndexPage,
}));
const NewTonKilometerPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).NewTonKilometerPage,
}));
const TonKilometerDetailPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerDetailPage,
}));
const TonKilometerPropertiesPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerPropertiesPage,
}));
const TonKilometerPropertyPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerPropertyPage,
}));
const TonKilometerTransferPage = lazy(async () => ({
  default: (await import("./pages/TonKilometerPages")).TonKilometerTransferPage,
}));
const AssessmentRollListPage = lazy(async () => ({
  default: (await import("./pages/AssessmentRollPages")).AssessmentRollListPage,
}));
const NewAssessmentRollPage = lazy(async () => ({
  default: (await import("./pages/AssessmentRollPages")).NewAssessmentRollPage,
}));
const AssessmentRollDetailPage = lazy(async () => ({
  default: (await import("./pages/AssessmentRollPages")).AssessmentRollDetailPage,
}));
const MemberInvoiceListPage = lazy(async () => ({
  default: (await import("./pages/MemberInvoicePages")).MemberInvoiceListPage,
}));
const NewMemberInvoiceBatchPage = lazy(async () => ({
  default: (await import("./pages/MemberInvoicePages")).NewMemberInvoiceBatchPage,
}));
const NewManualMemberInvoicePage = lazy(async () => ({
  default: (await import("./pages/MemberInvoicePages")).NewManualMemberInvoicePage,
}));
const MemberInvoiceDetailPage = lazy(async () => ({
  default: (await import("./pages/MemberInvoicePages")).MemberInvoiceDetailPage,
}));
const BankTransactionListPage = lazy(async () => ({
  default: (await import("./pages/BankTransactionPages")).BankTransactionListPage,
}));
const BankStatementImportPage = lazy(async () => ({
  default: (await import("./pages/BankTransactionPages")).BankStatementImportPage,
}));
const BankTransactionDetailPage = lazy(async () => ({
  default: (await import("./pages/BankTransactionPages")).BankTransactionDetailPage,
}));
const MeetingListPage = lazy(async () => ({
  default: (await import("./pages/MeetingPages")).MeetingListPage,
}));
const NewMeetingPage = lazy(async () => ({
  default: (await import("./pages/MeetingPages")).NewMeetingPage,
}));
const MeetingDetailPage = lazy(async () => ({
  default: (await import("./pages/MeetingPages")).MeetingDetailPage,
}));
const CaseListPage = lazy(async () => ({
  default: (await import("./pages/CasePages")).CaseListPage,
}));
const NewCasePage = lazy(async () => ({
  default: (await import("./pages/CasePages")).NewCasePage,
}));
const CaseDetailPage = lazy(async () => ({
  default: (await import("./pages/CasePages")).CaseDetailPage,
}));

type RouterContext = { queryClient: QueryClient };
function DocumentTitle() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  useEffect(() => {
    const tenantPathname = pathname.startsWith("/app/upload")
      ? "/app/upload"
      : pathname.replace(/^\/app\/[^/]+/, "/app");
    const routes: Array<[RegExp, string]> = [
      [/^\/login$/, "Logga in"], [/^\/acceptera-inbjudan$/, "Acceptera inbjudan"],
      [/^\/aktivera-styrelse$/, "Aktivera styrelsekonto"],
      [/^\/app\/upload$/, "Skicka in underlag"],
      [/^\/app\/dashboard$/, "Översikt"], [/^\/app\/verifikationer\/ny$/, "Ny verifikation"],
      [/^\/app\/verifikationer\/.+$/, "Verifikation"], [/^\/app\/verifikationer$/, "Verifikationer"],
      [/^\/app\/leverantorsfakturor\/underlag$/, "Underlag"],
      [/^\/app\/leverantorsfakturor\/ny$/, "Ny leverantörsfaktura"],
      [/^\/app\/leverantorsfakturor\/.+$/, "Leverantörsfaktura"],
      [/^\/app\/leverantorsfakturor$/, "Leverantörsfakturor"],
      [/^\/app\/fakturering\/skapa\/.+\/.+$/, "Skapa medlemsfakturor"],
      [/^\/app\/fakturering\/ny$/, "Ny medlemsfaktura"],
      [/^\/app\/fakturering\/.+$/, "Medlemsfaktura"],
      [/^\/app\/fakturering$/, "Fakturering"],
      [/^\/app\/bankhandelser\/importera$/, "Importera bankhändelser"],
      [/^\/app\/bankhandelser\/.+$/, "Bankhändelse"],
      [/^\/app\/bankhandelser$/, "Bankhändelser"],
      [/^\/app\/moten\/ny$/, "Nytt möte"],
      [/^\/app\/moten\/.+$/, "Möte"],
      [/^\/app\/moten$/, "Möten"],
      [/^\/app\/arenden\/ny$/, "Nytt ärende"],
      [/^\/app\/arenden\/.+$/, "Ärende"],
      [/^\/app\/arenden$/, "Ärenden"],
      [/^\/app\/rapporter$/, "Rapporter"],
      [/^\/app\/rakenskapsar\/.+\/ingaende-balans$/, "Redigera ingående balans"],
      [/^\/app\/rakenskapsar$/, "Räkenskapsår"],
      [/^\/app\/forening\/agare\/.+$/, "Ägare"], [/^\/app\/forening\/agare$/, "Ägare"],
      [/^\/app\/forening\/fastigheter\/.+$/, "Fastighet"], [/^\/app\/forening\/fastigheter$/, "Fastigheter"],
      [/^\/app\/forening\/debiteringslangder\/ny$/, "Ny debiteringslängd"],
      [/^\/app\/forening\/debiteringslangder\/[^/]+\/[^/]+$/, "Debiteringslängd"],
      [/^\/app\/forening\/debiteringslangder$/, "Debiteringslängder"],
      [/^\/app\/forening\/tonkilometerberakningar\/ny$/, "Ny tonkilometerberäkning"],
      [/^\/app\/forening\/tonkilometerberakningar\/[^/]+\/fastigheter$/, "Fastigheter och trafikslag"],
      [/^\/app\/forening\/tonkilometerberakningar\/[^/]+$/, "Tonkilometerberäkning"],
      [/^\/app\/forening\/tonkilometerberakningar$/, "Tonkilometerberäkningar"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar\/ny$/, "Ny tonkilometerberäkning"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar\/[^/]+\/overfor$/, "Överför driftandelstal"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar\/[^/]+\/fastigheter\/[^/]+$/, "Fastighetens trafikslag"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar\/[^/]+\/fastigheter$/, "Fastigheter och trafikslag"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar\/[^/]+$/, "Tonkilometerberäkning"],
      [/^\/app\/forening\/anlaggningar\/[^/]+\/tonkilometerberakningar$/, "Tonkilometerberäkningar"],
      [/^\/app\/forening\/anlaggningar\/.+$/, "Anläggning"], [/^\/app\/forening\/anlaggningar$/, "Anläggningar"],
      [/^\/app\/installningar\/anvandare$/, "Användare"],
      [/^\/app\/installningar\/kontoplan$/, "Kontoplan"],
      [/^\/app\/installningar\/transaktionskonton\/ny$/, "Nytt transaktionskonto"],
      [/^\/app\/installningar\/transaktionskonton\/.+$/, "Transaktionskonto"],
      [/^\/app\/installningar\/transaktionskonton$/, "Transaktionskonton"],
      [/^\/app\/installningar\/styrelse$/, "Styrelse"],
      [/^\/app\/installningar$/, "Inställningar"],
    ];
    routes.unshift([/^\/app\/forening\/vagar$/, "Vägar"]);
    const title = routes.find(([pattern]) => pattern.test(tenantPathname))?.[1] ?? "VägAdmin";
    document.title = title === "VägAdmin" ? title : `${title} | VägAdmin`;
  }, [pathname]);
  return null;
}
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <><DocumentTitle /><Suspense
      fallback={
        <div className="spinner" role="status" aria-label="Laddar sida" />
      }
    >
      <Outlet />
    </Suspense></>
  ),
});
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/app" });
  },
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/acceptera-inbjudan",
  component: AcceptInvitationPage,
});
const boardActivationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/aktivera-styrelse",
  component: BoardActivationPage,
});
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
});
const appLandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  beforeLoad: async ({ context }) => {
    let identity;
    try {
      identity = await context.queryClient.ensureQueryData(identityQueryOptions());
    } catch (error) {
      if (error instanceof ApiError && error.status === 401)
        throw redirect({ to: "/login" });
      throw error;
    }
    const firstCompany = identity.companies[0];
    if (!firstCompany) throw redirect({ to: "/login" });
    throw redirect({
      to: "/app/$companyId/dashboard",
      params: { companyId: firstCompany.id },
    });
  },
});
const uploadEntryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "upload",
  beforeLoad: async ({ context }) => {
    rememberPostLoginReturn(window.location.href);
    try {
      await context.queryClient.ensureQueryData(identityQueryOptions());
      discardPostLoginReturn();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        throw redirect({ to: "/login" });
      }
      throw error;
    }
  },
  component: UploadEntryPage,
});
const uploadCompanyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "upload/$companyId",
  beforeLoad: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(companyMeQueryOptions(params.companyId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        rememberPostLoginReturn(`/app/upload${window.location.search}`);
        throw redirect({ to: "/login" });
      }
      if (error instanceof ApiError && error.status === 404)
        throw redirect({ to: "/app/upload" });
      throw error;
    }
  },
  component: QuickDocumentUploadPage,
});
const companyAppRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "$companyId",
  beforeLoad: async ({ context, params }) => {
    try {
      await context.queryClient.ensureQueryData(companyMeQueryOptions(params.companyId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401)
        throw redirect({ to: "/login" });
      if (error instanceof ApiError && error.status === 404)
        throw redirect({ to: "/app" });
      throw error;
    }
  },
  component: AppShell,
});
const appIndex = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/$companyId/dashboard",
      params: { companyId: params.companyId },
    });
  },
});
const dashboard = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "dashboard",
  component: DashboardPage,
});
const vouchers = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "verifikationer",
  component: VoucherListPage,
});
const newVoucher = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "verifikationer/ny",
  component: VoucherEditorPage,
});
const voucher = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "verifikationer/$voucherId",
  component: VoucherEditorPage,
});
const invoices = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "leverantorsfakturor",
  component: InvoiceListPage,
});
const newInvoice = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "leverantorsfakturor/ny",
  component: InvoiceEditorPage,
});
const invoice = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "leverantorsfakturor/$invoiceId",
  component: InvoiceEditorPage,
});
const underlag = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "leverantorsfakturor/underlag",
  component: DocumentUploadPage,
});
const legacyQuickUpload = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "snabbuppladdning",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/upload/$companyId",
      params: { companyId: params.companyId },
    });
  },
});
const memberInvoices = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "fakturering",
  component: MemberInvoiceListPage,
});
const newMemberInvoiceBatch = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "fakturering/skapa/$facilityId/$rollId",
  component: NewMemberInvoiceBatchPage,
});
const newManualMemberInvoice = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "fakturering/ny",
  component: NewManualMemberInvoicePage,
});
const memberInvoice = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "fakturering/$invoiceId",
  component: MemberInvoiceDetailPage,
});
const bankTransactions = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "bankhandelser",
  component: BankTransactionListPage,
});
const bankStatementImport = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "bankhandelser/importera",
  component: BankStatementImportPage,
});
const bankTransaction = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "bankhandelser/$bankTransactionId",
  component: BankTransactionDetailPage,
});
const meetings = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "moten",
  component: MeetingListPage,
});
const newMeeting = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "moten/ny",
  component: NewMeetingPage,
});
const meeting = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "moten/$meetingId",
  component: MeetingDetailPage,
});
const cases = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "arenden",
  component: CaseListPage,
});
const newCase = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "arenden/ny",
  component: NewCasePage,
});
const caseDetail = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "arenden/$caseId",
  component: CaseDetailPage,
});
const reports = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "rapporter",
  component: ReportsPage,
});
const fiscalYears = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "rakenskapsar",
  component: FiscalYearsPage,
});
const openingBalanceEdit = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "rakenskapsar/$fiscalYearId/ingaende-balans",
  component: OpeningBalanceEditPage,
});
const settings = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar",
  component: SettingsPage,
});
const users = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/anvandare",
  component: UsersPage,
});
const chartOfAccounts = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/kontoplan",
  component: ChartOfAccountsPage,
});
const transactionAccounts = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/transaktionskonton",
  component: TransactionAccountListPage,
});
const newTransactionAccount = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/transaktionskonton/ny",
  component: NewTransactionAccountPage,
});
const transactionAccount = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/transaktionskonton/$transactionAccountId",
  component: TransactionAccountDetailPage,
});
const board = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "installningar/styrelse",
  component: BoardPage,
});
const associationIndex = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/$companyId/forening/agare",
      params: { companyId: params.companyId },
    });
  },
});
const owners = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/agare",
  component: OwnersPage,
});
const owner = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/agare/$ownerId",
  component: OwnerDetailPage,
});
const properties = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/fastigheter",
  component: PropertiesPage,
});
const property = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/fastigheter/$propertyId",
  component: PropertyDetailPage,
});
const roads = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/vagar",
  component: RoadsPage,
  validateSearch: (search: Record<string, unknown>): { q?: string | undefined; surface?: "gravel" | "asphalt" | undefined; road?: string | undefined } => ({
    ...(typeof search.q === "string" ? { q: search.q.slice(0, 200) } : {}),
    ...(search.surface === "gravel" || search.surface === "asphalt" ? { surface: search.surface } : {}),
    ...(typeof search.road === "string" && (search.road === "new" || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(search.road)) ? { road: search.road } : {}),
  }),
});
const facilities = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar",
  component: FacilitiesPage,
});
const facility = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId",
  component: FacilityDetailPage,
});
const tonKilometerIndex = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/tonkilometerberakningar",
  component: TonKilometerIndexPage,
});
const newTonKilometerIndex = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/tonkilometerberakningar/ny",
  component: NewTonKilometerIndexPage,
});
const standaloneTonKilometerCalculation = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/tonkilometerberakningar/$calculationId",
  component: TonKilometerDetailPage,
});
const standaloneTonKilometerProperties = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/tonkilometerberakningar/$calculationId/fastigheter",
  component: TonKilometerPropertiesPage,
});
const tonKilometerCalculations = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar",
  component: TonKilometerListPage,
});
const newTonKilometerCalculation = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar/ny",
  component: NewTonKilometerPage,
});
const tonKilometerCalculation = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar/$calculationId",
  component: TonKilometerDetailPage,
});
const tonKilometerProperties = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar/$calculationId/fastigheter",
  component: TonKilometerPropertiesPage,
});
const tonKilometerProperty = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar/$calculationId/fastigheter/$facilityPropertyId",
  component: TonKilometerPropertyPage,
});
const tonKilometerTransfer = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/anlaggningar/$facilityId/tonkilometerberakningar/$calculationId/overfor",
  component: TonKilometerTransferPage,
});
const assessmentRolls = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/debiteringslangder",
  component: AssessmentRollListPage,
});
const newAssessmentRoll = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/debiteringslangder/ny",
  component: NewAssessmentRollPage,
});
const assessmentRoll = createRoute({
  getParentRoute: () => companyAppRoute,
  path: "forening/debiteringslangder/$facilityId/$rollId",
  component: AssessmentRollDetailPage,
});
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  inviteRoute,
  boardActivationRoute,
  appRoute.addChildren([
    appLandingRoute,
    uploadEntryRoute,
    uploadCompanyRoute,
    companyAppRoute.addChildren([
      appIndex,
      dashboard,
      vouchers,
      newVoucher,
      voucher,
      invoices,
      newInvoice,
      invoice,
      underlag,
      legacyQuickUpload,
      memberInvoices,
      newMemberInvoiceBatch,
      newManualMemberInvoice,
      memberInvoice,
      bankTransactions,
      bankStatementImport,
      bankTransaction,
      meetings,
      newMeeting,
      meeting,
      cases,
      newCase,
      caseDetail,
      reports,
      fiscalYears,
      openingBalanceEdit,
      settings,
      users,
      chartOfAccounts,
      transactionAccounts,
      newTransactionAccount,
      transactionAccount,
      board,
      associationIndex,
      owners,
      owner,
      properties,
      property,
      roads,
      facilities,
      facility,
      tonKilometerIndex,
      newTonKilometerIndex,
      standaloneTonKilometerCalculation,
      standaloneTonKilometerProperties,
      tonKilometerCalculations,
      newTonKilometerCalculation,
      tonKilometerCalculation,
      tonKilometerProperties,
      tonKilometerProperty,
      tonKilometerTransfer,
      assessmentRolls,
      newAssessmentRoll,
      assessmentRoll,
    ]),
  ]),
]);
export const router = createRouter({
  routeTree,
  context: { queryClient: undefined! },
  defaultPreload: "intent",
  defaultPendingMs: 0,
  defaultPendingMinMs: 0,
  defaultPendingComponent: () => (
    <div className="spinner" role="status" aria-label="Laddar sida" />
  ),
});
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
